import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const categories = ["all", "fashion", "electronics", "lifestyle"];

export function ProductGrid({ products, activeCategory, onCategory, onQuickView }) {
  const { addToCart } = useStore();
  return (
    <section id="shop" className="px-5 py-28 lg:px-10 lg:py-36" data-testid="product-collection">
      <div className="mb-16 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-neon">01 / The collection</p>
          <h2 className="mt-4 font-heading text-5xl font-black italic tracking-tighter md:text-7xl">Objects with a pulse.</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category}
              data-testid={`category-filter-${category}`}
              onClick={() => onCategory(category)}
              className={`kinetic-button border px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${activeCategory === category ? "border-volt bg-volt text-black" : "border-white/15 text-zinc-400 hover:text-black"}`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-4">
        {products.map((product, index) => (
          <motion.article
            key={product.id}
            data-testid={`product-card-${product.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
            className={`group ${index % 4 === 1 ? "lg:mt-20" : index % 4 === 3 ? "lg:mt-10" : ""}`}
          >
            <button data-testid={`quick-view-${product.id}`} onClick={() => onQuickView(product)} className="relative block w-full text-left">
              <div className={`relative aspect-[3/4] overflow-hidden border border-white/10 bg-zinc-900 ${index % 2 === 0 ? "rounded-t-full" : "product-frame"}`}>
                <img src={product.image_url} alt={product.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                {product.badge && <Badge data-testid={`product-badge-${product.id}`} className="absolute left-4 top-4 rounded-none bg-volt text-black hover:bg-volt">{product.badge}</Badge>}
              </div>
              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">{product.category}</p>
                  <h3 className="mt-2 font-subheading text-xl font-bold tracking-tight text-zinc-100">{product.name}</h3>
                </div>
                <p data-testid={`product-price-${product.id}`} className="font-subheading text-lg font-bold text-volt">{formatCurrency(product.price)}</p>
              </div>
            </button>
            <Button data-testid={`add-to-cart-${product.id}`} onClick={() => addToCart(product)} disabled={product.stock === 0} className="kinetic-button mt-4 h-11 w-full rounded-none border border-white/15 bg-transparent font-bold uppercase tracking-[0.18em] hover:text-black">
              <Plus size={16} /> {product.stock === 0 ? "Sold out" : "Add to cart"}
            </Button>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
